import { Lock, Star, ArrowRight, Check, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface OnboardingUpgradeBlockProps {
  title: string;
  description: string;
  features?: string[];
  onUpgrade?: () => void;
  onSkip?: () => void;
  onReturnHome?: () => void;
}

export function OnboardingUpgradeBlock({
  title,
  description,
  features = [],
  onUpgrade,
  onSkip,
  onReturnHome,
}: OnboardingUpgradeBlockProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg border-2 border-amber-200 shadow-2xl">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-amber-100 rounded-full blur-2xl opacity-50"></div>
              <Lock className="w-16 h-16 text-amber-600 relative" />
            </div>
          </div>
          <div>
            <CardTitle className="text-2xl text-amber-900 mb-2">
              🔐 {title}
            </CardTitle>
            <p className="text-sm text-amber-800">{description}</p>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Features */}
          {features.length > 0 && (
            <div className="bg-amber-50 rounded-lg p-4 space-y-3">
              <p className="text-sm font-semibold text-amber-900">
                ✨ Desbloqueie com o Plano Profissional:
              </p>
              <ul className="space-y-2">
                {features.map((feature, index) => (
                  <li
                    key={index}
                    className="flex items-center gap-3 text-sm text-amber-800"
                  >
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Benefits Card */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-start gap-3">
              <Star className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
              <div className="text-sm">
                <p className="font-semibold text-blue-900 mb-1">
                  Por que fazer upgrade?
                </p>
                <ul className="text-blue-800 space-y-1 text-xs">
                  <li>• Acesso a todas as funcionalidades premium</li>
                  <li>• Suporte prioritário</li>
                  <li>• Melhor para crescimento do seu negócio</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={onUpgrade}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 rounded-lg shadow-lg hover:shadow-xl transition-all"
            >
              <span>Fazer Upgrade Agora</span>
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            {onReturnHome && (
              <Button
                onClick={onReturnHome}
                variant="outline"
                className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <Home className="w-4 h-4 mr-2" />
                Retornar para Tela Inicial
              </Button>
            )}

            {onSkip && (
              <Button
                onClick={onSkip}
                variant="outline"
                className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                Pular Esta Etapa
              </Button>
            )}
          </div>

          {/* Trust Badge */}
          <div className="text-center pt-4 border-t border-amber-100">
            <p className="text-xs text-gray-600">
              ✅ Teste grátis por 7 dias - Sem cartão de crédito necessário
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
